<script setup lang="ts">
  import { Button } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import { DEFAULT_TOURISTS, type TouristsSelection, type Selection } from '@/entities/searchCriteria';
  import { FieldSize } from '@/entities/searchForm';
  import { DateFromField, formatCheckInLabel } from '@/features/searchForm/dateFrom';
  import { DepartureCityField } from '@/features/searchForm/departureCity';
  import { DestinationField, type DestinationPrices } from '@/features/searchForm/destination';
  import { NightsRangeField } from '@/features/searchForm/nightsRange';
  import { TouristsRoomsField } from '@/features/searchForm/touristsRooms';
  import { useViewport, Breakpoint } from '@/shared/composables';
  import type { DepartureCity, NightsRange } from '@/shared/model';
  import { SEARCH_FORM_TEXTS } from '../config/texts';
  import { type SearchFormCriteria, type SearchFormExpose } from '../model/types';
  import { useCrossFieldCriteria } from '../model/useCrossFieldCriteria';
  import { useSearchFormData } from '../model/useSearchFormData';

  interface SearchFormProps {
    modelValue: SearchFormCriteria;
    prices?: DestinationPrices;
    destinationDisabled?: boolean;
    departureDisabled?: boolean;
    disableCriteriaDefaults?: boolean;
    isVisibleRoomButton?: boolean;
    isVisibleDeparture?: boolean;
    formUrl?: string;
    searchUrl?: string;
    resortsUrl?: string;
    size?: `${FieldSize}`;
    searchBtnText?: string;
    dateFieldLabel?: string;
    touristsFieldLabel?: string;
    touristsWordForms?: string[];
    nightsSummaryPrefix?: string;
    roundedBottom?: boolean;
  }

  const props = withDefaults(defineProps<SearchFormProps>(), {
    prices: undefined,
    destinationDisabled: false,
    departureDisabled: false,
    disableCriteriaDefaults: false,
    formUrl: '',
    searchUrl: '',
    resortsUrl: '',
    isVisibleDeparture: true,
    size: FieldSize.M,
    searchBtnText: SEARCH_FORM_TEXTS.searchBtn,
    dateFieldLabel: undefined,
    touristsFieldLabel: undefined,
    touristsWordForms: undefined,
    nightsSummaryPrefix: undefined,
  });

  interface SearchFormEmits {
    (e: 'update:modelValue', searchCriteria: SearchFormCriteria): void;
    (e: 'search', searchCriteria: SearchFormCriteria): void;
  }

  const emit = defineEmits<SearchFormEmits>();

  const { countries, cities, defaultCriteria } = useSearchFormData(props.formUrl);
  const { isSmaller } = useViewport();

  const DEFAULT_NIGHTS: NightsRange = { from: 7, to: 10 };
  const MODEL_VALUE_EVENT = 'update:modelValue';
  function defaultTourists(): TouristsSelection {
    return DEFAULT_TOURISTS;
  }

  const dateRange = computed({
    get: () => props.modelValue.dateRange,
    set: (value) => emit(MODEL_VALUE_EVENT, { ...props.modelValue, dateRange: value }),
  });
  const tourists = computed({
    get: () => props.modelValue.tourists || defaultTourists(),
    set: (value) => emit(MODEL_VALUE_EVENT, { ...props.modelValue, tourists: value }),
  });
  const nights = computed({
    get: () => props.modelValue.nights || DEFAULT_NIGHTS,
    set: (value) => emit(MODEL_VALUE_EVENT, { ...props.modelValue, nights: value }),
  });
  function resolveDefaults(city: DepartureCity | null, selection: Selection | null): Partial<SearchFormCriteria> {
    return props.disableCriteriaDefaults ? {} : criteria.resolveDefaults(city, selection);
  }

  const fromCity = computed({
    get: () => props.modelValue.fromCity,
    set: (value) => {
      emit(MODEL_VALUE_EVENT, {
        ...props.modelValue,
        fromCity: value,
        ...resolveDefaults(value, destination.value),
      });
    },
  });
  const destination = computed({
    get: () => props.modelValue.destination,
    set: (value: Selection | null) => {
      if (value?.kind === 'none') return;
      emit(MODEL_VALUE_EVENT, {
        ...props.modelValue,
        destination: value,
        ...resolveDefaults(fromCity.value, value),
      });
    },
  });

  const isAdaptive = isSmaller(Breakpoint.L);

  const searchButtonText = computed(() => (isAdaptive.value ? '' : props.searchBtnText));

  const criteria = useCrossFieldCriteria({
    countries,
    defaultCriteria,
    destination,
    nights,
    dateRange,
  });

  const checkInLabel = computed<string | undefined>(() =>
    dateRange.value ? formatCheckInLabel(dateRange.value) : undefined,
  );

  type ValidatableField = { validate: () => boolean } | null;
  type DestinationFieldRef = { validate: () => boolean; clear: () => void } | null;
  const fromCityRef = ref<ValidatableField>(null);
  const destinationRef = ref<DestinationFieldRef>(null);
  const dateFromRef = ref<ValidatableField>(null);
  const nightsRef = ref<ValidatableField>(null);
  const touristsRef = ref<ValidatableField>(null);

  function validate(): boolean {
    const results = [
      fromCityRef.value?.validate() ?? true,
      destinationRef.value?.validate() ?? true,
      dateFromRef.value?.validate() ?? true,
      nightsRef.value?.validate() ?? true,
      touristsRef.value?.validate() ?? true,
    ];
    return results.every(Boolean);
  }

  function submit(): void {
    emit('search', props.modelValue);
  }

  function reset(): void {
    destinationRef.value?.clear();
    emit(MODEL_VALUE_EVENT, {
      ...props.modelValue,
      fromCity: null,
      destination: null,
      nights: { ...DEFAULT_NIGHTS },
      dateRange: null,
      tourists: defaultTourists(),
    });
    criteria.reset();
  }

  defineExpose<SearchFormExpose>({
    validate,
    submit,
    reset,
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    :class="['search-form', `search-form_size-${size}`, { 'search-form_rounded-bottom': roundedBottom }]"
    data-testid="search-form"
  >
    <div class="search-form__bar">
      <div :class="['search-form__card']">
        <div
          v-if="isVisibleDeparture"
          class="search-form__cell search-form__cell_origin"
        >
          <DepartureCityField
            ref="fromCityRef"
            v-model="fromCity"
            :size="size"
            :cities="cities"
            :disabled="departureDisabled"
          />
        </div>

        <span
          v-if="isVisibleDeparture"
          class="search-form__separator"
          aria-hidden="true"
        />

        <div
          :class="[
            'search-form__cell',
            'search-form__cell_destination',
            {
              'search-form__cell_destination-no-origin': !isVisibleDeparture,
            },
          ]"
        >
          <DestinationField
            ref="destinationRef"
            v-model="destination"
            :size="size"
            :countries="countries"
            :disabled="destinationDisabled"
            :prices="prices"
            :search-url="searchUrl"
            :resorts-url="resortsUrl"
          />
        </div>

        <span
          class="search-form__separator"
          aria-hidden="true"
        />

        <div class="search-form__cell search-form__cell_date">
          <DateFromField
            ref="dateFromRef"
            v-model="dateRange"
            :size="size"
            :field-label="dateFieldLabel"
            @manual="criteria.markTouchedDate"
          />
        </div>

        <span
          class="search-form__separator"
          aria-hidden="true"
        />

        <div class="search-form__cell search-form__cell_nights">
          <NightsRangeField
            ref="nightsRef"
            v-model="nights"
            :size="size"
            :check-in-label="checkInLabel"
            :summary-prefix="nightsSummaryPrefix"
            @manual="criteria.markTouchedNights"
          />
        </div>

        <span
          class="search-form__separator"
          aria-hidden="true"
        />

        <div
          :class="[
            'search-form__cell',
            'search-form__cell_guests',
            {
              'search-form__cell_guests-no-rooms': !isVisibleRoomButton,
            },
          ]"
        >
          <TouristsRoomsField
            ref="touristsRef"
            v-model="tourists"
            :size="size"
            :is-visible-add-button="isVisibleRoomButton"
            :field-label="touristsFieldLabel"
            :tourists-word-forms="touristsWordForms"
          />
        </div>
        <div class="search-form__btn-container">
          <Button
            class="search-form__search-btn"
            data-testid="search-form-search-btn"
            appearance="primary"
            :icon-left="isAdaptive ? 'search' : undefined"
            @click="submit"
          >
            <template
              v-if="searchButtonText"
              #default
            >
              {{ searchButtonText }}
            </template>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .search-form {
    $root: &;

    background: linear-gradient(
      262deg,
      var(--color-cyan-40, #2ce) -14.86%,
      var(--color-brand-secondary, #218bff) 6.05%,
      var(--color-sky-50, #218bff) 112.65%
    );
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    padding: var(--spacing-m) 0 var(--spacing-l) 0;
    position: relative;
    width: 100%;
    z-index: 2;

    &_rounded-bottom {
      border-bottom-left-radius: var(--rounding-xl);
      border-bottom-right-radius: var(--rounding-xl);
    }

    &__bar {
      max-width: $max-width-xl;
      width: 100%;
    }

    &__card {
      align-items: center;
      background: var(--color-bg-primary);
      border-radius: var(--rounding-l);
      display: flex;
      flex: 1 1 auto;
      padding-right: var(--spacing-xs);
    }

    &__cell {
      flex-grow: 1;

      &_origin {
        flex-basis: 109px;
        flex-grow: 2.5;

        ::v-deep .field__control {
          border-radius: var(--rounding-l) 0 0 var(--rounding-l);
        }
      }

      &_destination {
        flex-basis: 171px;
        flex-grow: 4.4;

        &-no-origin {
          ::v-deep .field__control {
            border-radius: var(--rounding-l) 0 0 var(--rounding-l);
          }
        }
      }

      &_date {
        flex-basis: 124px;
        flex-grow: 1;
      }

      &_nights {
        flex-basis: 124px;
        flex-grow: 1;
      }

      &_guests {
        flex-basis: 116px;
        flex-grow: 1;
      }

      &_guests-no-rooms {
        flex-basis: 141px;
        flex-grow: 1.7;
      }
    }

    &__separator {
      align-self: center;
      background-color: var(--color-neutral-20);
      flex: 0 0 1px;
      height: 54px;
      width: 1px;
    }

    &__btn-container {
      box-sizing: border-box;
      max-width: 188px;
      padding-left: var(--spacing-xs);
      width: 100%;
    }

    &__search-btn {
      width: 100%;
    }

    &_size-l {
      #{$root}__search-btn {
        height: 48px;
      }
      #{$root}__separator {
        height: 64px;
      }
    }

    @media (max-width: $breakpoint-l) {
      &__btn-container {
        max-width: min-content;
        width: auto;
      }

      &__search-btn {
        width: auto;
      }
    }

    @include responsive-max-width('&__bar');
  }
</style>
